import React, { useState, useEffect } from "react";
import { Form, Select, Input, FormInstance } from "antd";
import { Country, State, City } from "country-state-city";

interface CountryStateInterface {
  initialCountry?: string;
  initialState?: string;
  initialCity?: string;
  initialPostalCode?: string;
  form?: FormInstance;
}

interface FormValues {
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
}

const CountryStateCityPicker: React.FC<CountryStateInterface> = ({
  initialCountry,
  initialState,
  initialCity,
  initialPostalCode,
  form,
}) => {
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState(initialCountry || "");
  const [selectedState, setSelectedState] = useState(initialState || "");
  const [selectedCity, setSelectedCity] = useState(initialCity || "");
  const [postalCode, setPostalCode] = useState(initialPostalCode || "");

  // Use the form passed from parent
  const formToUse = form;

  // Initialize countries
  useEffect(() => {
    const countryList = Country.getAllCountries().map((country) => ({
      value: country.isoCode,
      label: country.name,
      name: country.name,
    }));

    setCountries([
      { value: "", label: "Country", disabled: true },
      ...countryList,
    ]);
  }, []);

  // Helper function to update form values with delay
  const updateFormValues = (fields: FormValues) => {
    if (!formToUse) return;

    setTimeout(() => {
      formToUse.setFieldsValue(fields);
    }, 100);
  };

  // Find country code by name if needed
  const resolveCountryCode = (countryInput: string | undefined) => {
    if (!countryInput) return "";

    // If valid ISO code, use it directly
    if (Country.getCountryByCode(countryInput)) return countryInput;

    // Try to find by name
    const countryByName = countries.find((c) => c.label === countryInput);
    return countryByName ? countryByName.value : "";
  };

  // Set initial country and form values
  useEffect(() => {
    if (countries.length <= 1) return;

    const countryCode = resolveCountryCode(initialCountry);
    setSelectedCountry(countryCode);

    if (countryCode && formToUse) {
      updateFormValues({
        country: countryCode,
        state: selectedState,
        city: selectedCity,
        zip: postalCode,
      });
    }
  }, [initialCountry, countries, formToUse]);

  // Load states when country changes
  useEffect(() => {
    // Reset states if no country selected
    if (!selectedCountry) {
      setStates([{ value: "", label: "State/Province", disabled: true }]);
      setSelectedState("");
      return;
    }

    // Load states for this country
    const stateList = State.getStatesOfCountry(selectedCountry).map(
      (state) => ({
        value: state.isoCode,
        label: state.name,
        name: state.name,
      })
    );

    setStates([
      { value: "", label: "State/Province", disabled: true },
      ...stateList,
    ]);

    // Handle initial state value
    if (initialState && states.length <= 1) {
      let stateCode = initialState;

      // If not a valid ISO code, try by name
      if (!State.getStateByCodeAndCountry(initialState, selectedCountry)) {
        const stateByName = stateList.find((s) => s.label === initialState);
        if (stateByName) {
          stateCode = stateByName.value;
        }
      }

      setSelectedState(stateCode);

      if (formToUse && stateCode) {
        updateFormValues({ state: stateCode });
      }
    }
  }, [selectedCountry, initialState]);

  // Load cities when state changes
  useEffect(() => {
    // Reset cities if no state selected
    if (!selectedCountry || !selectedState) {
      setCities([{ value: "", label: "City", disabled: true }]);
      setSelectedCity("");
      return;
    }

    // Load cities for this state
    const cityList = City.getCitiesOfState(selectedCountry, selectedState).map(
      (city) => ({
        value: city.name,
        label: city.name,
      })
    );

    setCities([{ value: "", label: "City", disabled: true }, ...cityList]);

    // Handle initial city value
    if (initialCity && cities.length <= 1) {
      setSelectedCity(initialCity);

      if (formToUse) {
        updateFormValues({
          city: initialCity,
          zip: initialPostalCode || "",
        });
      }
    }
  }, [selectedCountry, selectedState, initialCity, cities.length]);

  // Update postal code
  useEffect(() => {
    if (initialPostalCode && formToUse) {
      setPostalCode(initialPostalCode);
      formToUse.setFieldsValue({ zip: initialPostalCode });
    }
  }, [initialPostalCode, formToUse]);

  // Clear dependent fields and update form
  const clearDependentFields = (level: "country" | "state") => {
    if (!formToUse) return;

    const updates: FormValues = { zip: "" };

    if (level === "country" || level === "state") {
      setSelectedCity("");
      updates.city = "";
    }

    if (level === "country") {
      setSelectedState("");
      updates.state = "";
    }

    setPostalCode("");

    formToUse.setFieldsValue(updates);
  };

  const handleCountryChange = (value: string) => {
    if (value !== selectedCountry) {
      clearDependentFields("country");
    }

    setSelectedCountry(value);
    if (formToUse) {
      formToUse.setFieldsValue({ country: value });
    }
  };

  const handleStateChange = (value: string) => {
    if (value !== selectedState) {
      clearDependentFields("state");
    }

    setSelectedState(value);
    if (formToUse) {
      formToUse.setFieldsValue({ state: value });
    }
  };

  const handleCityChange = (value: string) => {
    setSelectedCity(value);
    if (formToUse) {
      formToUse.setFieldsValue({ city: value });
    }
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPostalCode(value);
    if (formToUse) {
      formToUse.setFieldsValue({ zip: value });
    }
  };

  return (
    <div className="country-state-city-picker">
      <div className="d-flex gap-[20px] mb-[20px]">
        <Form.Item name="country" className="flex-1">
          <Select
            className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] py-[10px] px-[15px] border-1 border-transparent focus:border-white bg-[#013351] placeholder-[#005A90] color-[#fff] h-auto w-full"
            options={countries}
            onChange={handleCountryChange}
            value={selectedCountry}
            placeholder="Country"
          />
        </Form.Item>
        <Form.Item name="state" className="flex-1">
          <Select
            className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] py-[10px] px-[15px] border-1 border-transparent focus:border-white bg-[#013351] placeholder-[#005A90] color-[#fff] h-auto w-full"
            options={states}
            onChange={handleStateChange}
            disabled={!selectedCountry}
            value={selectedState}
            placeholder="State/Province"
          />
        </Form.Item>
      </div>

      <div className="d-flex gap-[20px]">
        <Form.Item name="city" className="flex-1">
          <Select
            className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] py-[10px] px-[15px] border-1 border-transparent focus:border-white size-full bg-[#013351] placeholder-[#005A90] color-[#fff] h-auto"
            options={cities}
            onChange={handleCityChange}
            disabled={!selectedState}
            value={selectedCity}
            placeholder="City"
          />
        </Form.Item>
        <Form.Item name="zip">
          <Input
            placeholder="Postal Code"
            className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[14px] border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff]"
            value={postalCode}
            onChange={handlePostalCodeChange}
          />
        </Form.Item>
      </div>
    </div>
  );
};

export default CountryStateCityPicker;
